import invariant from 'invariant';
import { ASTExpr, ASTIdentifier } from './ast';
import { LiteralType } from './literal';
import { parseExpression } from './parser';
import { rewriteExprType } from './transform';

// A tagged template for LScript literals in the codebase. Examples:
//
//     l`3 + 4`
//     l`${fuelEconomy} * ${miles}`
//     l`ref(${slug}, ${column}, country="US")`
//
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals#tagged_templates
export function l(
  strings: TemplateStringsArray,
  ...exprsOrLits: Array<ASTExpr | LiteralType>
): ASTExpr {
  // These should be guaranteed by spec but I'm paranoid
  invariant(
    strings.length === exprsOrLits.length + 1,
    'tagged template is wrong'
  );
  invariant(strings.length > 0, 'tagged template is wrong');

  // First, turn all of the literals into equivalent AST expressions
  const exprs = exprsOrLits.map((e): ASTExpr => {
    if (e === null) {
      return { t: 'null' };
    }
    switch (typeof e) {
      case 'string':
        return {
          t: 'string',
          v: e,
        };
      case 'number':
        return {
          t: 'number',
          v: e,
        };
      case 'boolean':
        return {
          t: 'boolean',
          v: e,
        };
      default:
        if (!e.t) {
          throw new Error(
            `Non-expression passed in template function: ${e} (type: ${typeof e})`
          );
        }
        return e;
    }
  });

  // Then, assemble an LScript expression string. Our strategy here is:
  // - Generate a unique variable name for each expression
  // - Blindly join the string literals together with the variable names between
  //   them and hope it works out
  // - Parse the result as an LScript expression
  // - Substitute the generated variables with their corresponding expressions
  // It's theoretically possible to write a parser that can do this without
  // lowering everything to strings, but that's a lot of work. This is much
  // easier!
  // (an example where it won't work out: someone puts a variable substitution
  // within a string literal, like: l`"${4}"`)
  const prefix = sortaRandomIdentifier();
  let exprStr = strings[0];
  const varMap = new Map<string, ASTExpr>();
  for (let i = 0; i < exprs.length; i++) {
    const varName = `tpl_${prefix}_${i}`;
    varMap.set(varName, exprs[i]);
    exprStr += ` ${varName} ${strings[i + 1]}`;
  }

  const ast = parseExpression(exprStr);
  const rewritten = rewriteExprType<ASTIdentifier>(ast, 'identifier', (id) => {
    return varMap.get(id.name) ?? id;
  });

  return rewritten;
}

const alphabet = 'abcdefghijklmnopqrstuvwxyz';
function sortaRandomIdentifier(): string {
  let out = '';
  for (let i = 0; i < 8; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}
